import React, { useEffect, useRef } from 'react';
import ApexCharts from 'apexcharts';
import outcomesData from './outcomes.json';
import { Wrench, Microscope, Palette, Users, Briefcase, ClipboardList, Zap, RefreshCw, Shield, Target } from 'lucide-react';

const CareerResults = ({ scores }) => {
    const chartRefRIASEC = useRef(null);
    const chartRefPARD = useRef(null);

    const [formSubmitted, setFormSubmitted] = React.useState(false);
    const [formData, setFormData] = React.useState({
        name: '',
        phone: '',
        age: '',
        gender: ''
    });
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    // Prepare data for Part A (RIASEC)
    const partAData = scores.parta || {};
    // Map Part A codes to Labels
    const labelsA = {
        'R': 'Realistic',
        'I': 'Investigative',
        'A': 'Artistic',
        'S': 'Social',
        'E': 'Enterprising',
        'C': 'Conventional'
    };

    // Color scheme for each RIASEC trait
    const colorsA = {
        'R': '#22c55e', // Green - Realistic (hands-on, practical)
        'I': '#3b82f6', // Blue - Investigative (analytical, curious)
        'A': '#ec4899', // Pink - Artistic (creative, expressive)
        'S': '#f97316', // Orange - Social (helpful, cooperative)
        'E': '#8b5cf6', // Purple - Enterprising (persuasive, ambitious)
        'C': '#14b8a6'  // Teal - Conventional (organized, detail-oriented)
    };

    // Lucide Icons for each RIASEC trait
    const iconsA = {
        'R': Wrench,        // Wrench - Realistic
        'I': Microscope,    // Microscope - Investigative
        'A': Palette,       // Palette - Artistic
        'S': Users,         // Users - Social
        'E': Briefcase,     // Briefcase - Enterprising
        'C': ClipboardList  // ClipboardList - Conventional
    };
    const orderA = ['R', 'I', 'A', 'S', 'E', 'C'];
    // Normalize Part A for chart display: max score per type = 1
    // Scale to 0-10: (score / 1) * 10 = score * 10
    const dataA = orderA.map(cat => (partAData[cat] || 0) * 10);
    const labelsAForChart = orderA.map(k => labelsA[k]);

    // Prepare data for Part B (PARD)
    const partBData = scores.partb || {};
    // Map Part B codes to Labels
    const labelsB = {
        'P': 'Confidence',
        'A': 'Adaptability',
        'R': 'Resilience',
        'D': 'Determination'
    };

    // Color scheme for each PARD trait
    const colorsB = {
        'P': '#3b82f6', // Blue - Confidence
        'A': '#22c55e', // Green - Adaptability
        'R': '#f97316', // Orange - Resilience
        'D': '#8b5cf6'  // Purple - Determination
    };

    // Lucide Icons for each PARD trait
    const iconsB = {
        'P': Zap,         // Zap - Confidence
        'A': RefreshCw,   // RefreshCw - Adaptability
        'R': Shield,      // Shield - Resilience
        'D': Target       // Target - Determination
    };

    const orderB = ['P', 'A', 'R', 'D'];
    // Normalize Part B for chart display: max score per type = 3
    // Scale to 0-10: (score / 3) * 10
    const dataB = orderB.map(cat => ((partBData[cat] || 0) / 3) * 10);
    const labelsBForChart = orderB.map(k => labelsB[k]);

    // --- Advanced Logic ---

    // 1. Calculate Interest Clarity (RIASEC)
    const getInterestClarity = () => {
        // dataA is normalized 0-10
        const categoriesA = orderA; // Use orderA as categories
        const maxScore = Math.max(...dataA);
        const isClear = maxScore >= 7.0;

        // Find top traits
        const traitsWithScores = categoriesA.map((cat, i) => ({
            cat,
            label: labelsA[cat],
            score: dataA[i]
        }));
        traitsWithScores.sort((a, b) => b.score - a.score);
        const topTraits = traitsWithScores.slice(0, 2);

        return { isClear, topTraits };
    };

    // 2. Calculate Behavioral Resilience (PARD)
    const getPardInsights = () => {
        const categoriesB = orderB; // Use orderB as categories
        let highCount = 0;
        const insights = categoriesB.map((cat, index) => {
            const score = dataB[index]; // Normalized 0-10
            let band = 'low';
            if (score >= 7.0) band = 'high';
            else if (score >= 4.0) band = 'medium';

            if (band === 'high') highCount++;

            return {
                code: cat,
                title: outcomesData.pard_traits[cat].title,
                text: outcomesData.pard_traits[cat].bands[band],
                band: band,
                score: score
            };
        });

        let resilienceLevel = 'low';
        if (highCount >= 3 || highCount === 4) resilienceLevel = 'strong';
        else if (highCount >= 1) resilienceLevel = 'developing';

        return { resilienceLevel, insights };
    };

    // 3. Determine Quadrant Outcome
    const { isClear, topTraits } = getInterestClarity();
    const { resilienceLevel, insights: pardInsights } = getPardInsights();

    let outcomeKey = 'support_oriented';

    if (resilienceLevel === 'low') {
        outcomeKey = 'support_oriented';
    } else if (isClear && resilienceLevel === 'strong') {
        outcomeKey = 'aligned_resilient';
    } else if (isClear && resilienceLevel === 'developing') {
        outcomeKey = 'aligned_developing';
    } else if (!isClear && resilienceLevel === 'strong') {
        outcomeKey = 'misaligned_capable';
    } else if (!isClear && resilienceLevel === 'developing') {
        outcomeKey = 'exploratory_unsettled';
    }

    const outcome = outcomesData.quadrant_outcomes[outcomeKey];

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'phone') {
            // Only allow numbers, max 10
            const numbersOnly = value.replace(/[^0-9]/g, '').slice(0, 10);
            setFormData(prev => ({ ...prev, [name]: numbersOnly }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.phone.length !== 10) {
            alert("Please enter a valid 10-digit phone number.");
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                post_name: formData.phone,
                json_body: {
                    ...formData,
                    scores: scores
                }
            };

            await fetch('https://yanchacms.metadei.in/wp-json/career-demo/v1/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            // Even if fetch fails, strictly speaking we might want to show results,
            // but usually we proceed. For now, assume success or proceed anyway.
            setFormSubmitted(true);
        } catch (error) {
            console.error('Submission failed', error);
            // Fallback to show results even if API fails?
            // Letting user see results is usually better than blocking.
            setFormSubmitted(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (!formSubmitted) return;

        // RIASEC Chart (Purple theme)
        if (chartRefRIASEC.current) {
            const optionsRIASEC = {
                series: [{
                    name: 'Interest Score',
                    data: dataA,
                }],
                chart: {
                    height: 400,
                    type: 'radar',
                    toolbar: { show: false }
                },
                title: {
                    text: 'Interest Profile',
                    align: 'center',
                    style: { fontFamily: 'Cardo, sans-serif', fontSize: '20px', fontWeight: 'bold', color: '#92278F' }
                },
                plotOptions: {
                    radar: {
                        size: 110,
                        offsetX: 0,
                        offsetY: 0,
                        polygons: {
                            strokeColors: '#e8e8e8',
                            connectorColors: '#e8e8e8'
                        }
                    }
                },
                xaxis: {
                    categories: labelsAForChart,
                    labels: {
                        show: false
                    }
                },
                yaxis: {
                    show: false,
                    min: -2,
                    max: 10,
                    tickAmount: 6
                },
                fill: {
                    opacity: 0.2,
                    colors: ['#92278F']
                },
                stroke: {
                    width: 3,
                    colors: ['#92278F']
                },
                markers: {
                    size: 5,
                    colors: ['#92278F'],
                    strokeColors: '#fff',
                    strokeWidth: 2
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return Math.round(val) + '%';
                        }
                    }
                }
            };

            const chartRIASEC = new ApexCharts(chartRefRIASEC.current, optionsRIASEC);
            chartRIASEC.render();
        }

        // PARD Chart (Orange theme)
        if (chartRefPARD.current) {
            const optionsPARD = {
                series: [{
                    name: 'Behavioral Score',
                    data: dataB,
                }],
                chart: {
                    height: 400,
                    type: 'radar',
                    toolbar: { show: false }
                },
                title: {
                    text: 'Behavioral Profile',
                    align: 'center',
                    style: { fontFamily: 'Cardo, sans-serif', fontSize: '20px', fontWeight: 'bold', color: '#F37741' }
                },
                plotOptions: {
                    radar: {
                        size: 120,
                        offsetX: 0,
                        offsetY: 0,
                        polygons: {
                            strokeColors: '#e8e8e8',
                            connectorColors: '#e8e8e8'
                        }
                    }
                },
                xaxis: {
                    categories: labelsBForChart,
                    labels: {
                        show: false
                    }
                },
                yaxis: {
                    show: false,
                    min: -2,
                    max: 10,
                    tickAmount: 6
                },
                fill: {
                    opacity: 0.2,
                    colors: ['#F37741']
                },
                stroke: {
                    width: 3,
                    colors: ['#F37741']
                },
                markers: {
                    size: 5,
                    colors: ['#F37741'],
                    strokeColors: '#fff',
                    strokeWidth: 2
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return Math.round(val) + '%';
                        }
                    }
                }
            };

            const chartPARD = new ApexCharts(chartRefPARD.current, optionsPARD);
            chartPARD.render();
        }

        return () => {
            // Cleanup will happen on unmount
        };
    }, [scores, formSubmitted]);

    if (!formSubmitted) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[80vh] px-4 w-full animate-fadeIn pb-20 pt-5">
                <div className="w-full max-w-md bg-white shadow-xl rounded-3xl p-8 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-3 bg-gradient-to-r from-[#F37741] to-[#92278F]"></div>
                    <h2 className="text-2xl font-bold text-center text-[#2d3748] mb-6 mt-4">Just one last step...</h2>
                    <p className="text-center text-gray-500 mb-8">Please enter your details to view your crystal report.</p>

                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Name</label>
                            <input
                                required
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full p-3 rounded-xl border border-gray-300 focus:border-[#92278F] focus:ring-1 focus:ring-[#92278F] outline-none transition-all"
                                placeholder="Your Name"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Phone Number</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-bold border-r border-gray-300 pr-2">+91</span>
                                <input
                                    required
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="w-full p-3 pl-14 rounded-xl border border-gray-300 focus:border-[#92278F] focus:ring-1 focus:ring-[#92278F] outline-none transition-all"
                                    placeholder="Your Phone Number"
                                    maxLength={10}
                                />
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="w-1/2">
                                <label className="block text-sm font-bold text-gray-700 mb-1">Age</label>
                                <input
                                    required
                                    type="number"
                                    name="age"
                                    value={formData.age}
                                    onChange={handleChange}
                                    className="w-full p-3 rounded-xl border border-gray-300 focus:border-[#92278F] focus:ring-1 focus:ring-[#92278F] outline-none transition-all"
                                    placeholder="Age"
                                />
                            </div>
                            <div className="w-1/2">
                                <label className="block text-sm font-bold text-gray-700 mb-1">Gender</label>
                                <select
                                    required
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleChange}
                                    className="w-full p-3 rounded-xl border border-gray-300 focus:border-[#92278F] focus:ring-1 focus:ring-[#92278F] outline-none transition-all bg-white"
                                >
                                    <option value="">Select</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`mt-6 w-full py-3 rounded-xl text-white font-bold text-lg shadow-md transition-transform transform active:scale-95 ${isSubmitting ? 'bg-gray-400 cursor-wait' : 'bg-gradient-to-r from-[#F37741] to-[#92278F] hover:shadow-lg'}`}
                        >
                            {isSubmitting ? 'Generating Report...' : 'View Results'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center min-h-[80vh] px-4 w-full animate-fadeIn pb-20 pt-5">
            <h1 className="large-gradient-heading text-xl md:text-3xl mb-4 text-center px-4">
                Congratulations you have completed a demo of the crystal report.
            </h1>
            <p className="text-gray-500 mb-8 text-center">Get in touch with us to get the full conclusive report. This is just a demo</p>

            <div className="flex flex-col w-full max-w-4xl justify-center items-center gap-8">

                {/* === RIASEC CARD === */}
                <div className="bg-white/90 backdrop-blur-sm shadow-xl rounded-3xl p-6 md:p-10 relative overflow-hidden w-full transition-shadow duration-300 hover:shadow-2xl border border-purple-50">
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-purple-500 to-[#92278F]"></div>

                    {/* RIASEC Radar Chart with Icon Labels */}
                    <div className="relative" style={{ minHeight: '400px' }}>
                        <div ref={chartRefRIASEC}></div>

                        {/* Icon Labels positioned around the chart */}
                        {[
                            { code: 'R', top: '50px', left: '50%', transform: 'translateX(-50%)' },           // Top
                            { code: 'I', top: '115px', left: 'calc(50% + 130px)', transform: 'translateX(-50%)' },  // Top-right
                            { code: 'A', top: '285px', left: 'calc(50% + 130px)', transform: 'translateX(-50%)' },  // Bottom-right
                            { code: 'S', top: '350px', left: '50%', transform: 'translateX(-50%)' },          // Bottom
                            { code: 'E', top: '285px', left: 'calc(50% - 130px)', transform: 'translateX(-50%)' },  // Bottom-left
                            { code: 'C', top: '115px', left: 'calc(50% - 130px)', transform: 'translateX(-50%)' }   // Top-left
                        ].map(({ code, top, left, transform }) => {
                            const Icon = iconsA[code];
                            return (
                                <div
                                    key={code}
                                    className="absolute flex items-center justify-center pointer-events-none"
                                    style={{ top, left, transform }}
                                >
                                    <Icon
                                        size={28}
                                        color={colorsA[code]}
                                        strokeWidth={2.5}
                                    />
                                </div>
                            );
                        })}
                    </div>


                    {/* Custom Color-Coded Legend */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8 px-2">
                        {orderA.map((code, index) => (
                            <div
                                key={code}
                                className="flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-105"
                                style={{
                                    backgroundColor: `${colorsA[code]}15`,
                                    border: `1px solid ${colorsA[code]}40`
                                }}
                            >
                                {React.createElement(iconsA[code], {
                                    size: 24,
                                    color: colorsA[code],
                                    strokeWidth: 2
                                })}
                                <div className="flex flex-col">
                                    <span
                                        className="text-sm font-bold"
                                        style={{ color: colorsA[code] }}
                                    >
                                        {labelsA[code]}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {Math.round(dataA[index])}%
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* RIASEC Text Results (Outcome + Key Interests) */}
                    <div className="border-t border-purple-100 pt-8">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#92278F] mb-4 text-center" style={{ fontFamily: 'Cardo, serif' }}>{outcome.label}</h2>
                        <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto text-justify leading-relaxed font-light">
                            {outcome.description}
                        </p>
                        <div className="flex flex-col items-center justify-center gap-4 bg-purple-50/50 rounded-2xl p-6">
                            <span className="text-xs font-bold text-purple-600 uppercase tracking-widest border-b border-purple-200 pb-1">Key Interests</span>
                            <div className="flex gap-4 flex-wrap justify-center">
                                {topTraits.map(t => (
                                    <div
                                        key={t.cat}
                                        className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm"
                                        style={{ border: `2px solid ${colorsA[t.cat]}` }}
                                    >
                                        {React.createElement(iconsA[t.cat], {
                                            size: 20,
                                            color: colorsA[t.cat],
                                            strokeWidth: 2
                                        })}
                                        <span
                                            className="text-sm font-bold"
                                            style={{ color: colorsA[t.cat] }}
                                        >
                                            {t.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* === PARD CARD === */}
                <div className="bg-white/90 backdrop-blur-sm shadow-xl rounded-3xl p-6 md:p-10 relative overflow-hidden w-full transition-shadow duration-300 hover:shadow-2xl border border-orange-50">
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-400 to-[#F37741]"></div>

                    {/* PARD Radar Chart with Icon Labels */}
                    <div className="relative" style={{ minHeight: '400px' }}>
                        <div ref={chartRefPARD}></div>

                        {/* Icon Labels positioned around the chart (4-point diamond) */}
                        {[
                            { code: 'P', top: '50px', left: '50%', transform: 'translateX(-50%)' },           // Top
                            { code: 'A', top: '200px', left: 'calc(50% + 130px)', transform: 'translateX(-50%)' },  // Right
                            { code: 'R', top: '350px', left: '50%', transform: 'translateX(-50%)' },          // Bottom
                            { code: 'D', top: '200px', left: 'calc(50% - 130px)', transform: 'translateX(-50%)' }   // Left
                        ].map(({ code, top, left, transform }) => {
                            const Icon = iconsB[code];
                            return (
                                <div
                                    key={code}
                                    className="absolute flex items-center justify-center pointer-events-none"
                                    style={{ top, left, transform }}
                                >
                                    <Icon
                                        size={28}
                                        color={colorsB[code]}
                                        strokeWidth={2.5}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    {/* Custom Color-Coded Legend for PARD */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8 px-2">
                        {orderB.map((code, index) => (
                            <div
                                key={code}
                                className="flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-105"
                                style={{
                                    backgroundColor: `${colorsB[code]}15`,
                                    border: `1px solid ${colorsB[code]}40`
                                }}
                            >
                                {React.createElement(iconsB[code], {
                                    size: 24,
                                    color: colorsB[code],
                                    strokeWidth: 2
                                })}
                                <div className="flex flex-col">
                                    <span
                                        className="text-sm font-bold"
                                        style={{ color: colorsB[code] }}
                                    >
                                        {labelsB[code]}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {Math.round(dataB[index])}%
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* PARD Text Results (Behavioral Insights) */}
                    <div className="border-t border-orange-100 pt-8">
                        <h3 className="text-3xl md:text-4xl font-bold text-[#F37741] mb-8 text-center" style={{ fontFamily: 'Cardo, serif' }}>Behavioral Insights</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                            {pardInsights.map((item) => (
                                <div key={item.code} className="w-full bg-[#FFF5F1] rounded-2xl p-6 border border-orange-100 hover:border-orange-200 transition-colors">
                                    <h4 className="text-lg md:text-xl font-bold text-[#F37741] mb-2 flex items-center gap-2">
                                        {/* Optional: Add icons based on code later if needed */}
                                        {item.title}
                                    </h4>
                                    <p className="text-base text-gray-700 leading-relaxed font-light">{item.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Footer Note */}
                <p className="text-xs text-center text-gray-400 mt-4 italic max-w-lg">
                    {outcomesData.footer_note}
                </p>

            </div>

            <div className="mt-12 flex flex-col gap-4 items-center">
                {/* <button className="rotating-border-button group hover:scale-105 transition-transform">
                    <span className="gradient-small px-8 py-2 text-xl">Take the complete survey</span>
                </button> */}

                {/* <button onClick={() => window.location.reload()} className="text-gray-400 hover:text-[#92278F] transition-colors underline text-sm">
                    Retake Demo Assessment
                </button> */}
            </div>
        </div>
    );
};

export default CareerResults;
